import { Card, CardContent } from "@/components/ui/card";
import { DollarSign } from "lucide-react";

export function BalanceTab({ round, pointValue }) {
  return (
    <Card className="card-default">
      <CardContent className="p-4">
        <h3 className="text-lg font-bold text-[#0F172A] mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-600" />
          Balance Economico
        </h3>
        
        {pointValue > 0 ? (() => {
          const hasUA = round?.matchups?.some(m => (m.units_advantage_total || 0) > 0);
          return (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-2 font-bold text-[#0F172A] sticky left-0 bg-white min-w-[80px]">Jugador</th>
                  {round?.players?.map(opponent => (
                    <th key={opponent.player_id} className="text-center py-3 px-1 font-semibold text-[#64748B] min-w-[50px] text-xs sm:text-sm">
                      <span className="truncate block max-w-[50px]" title={opponent.name}>{opponent.name?.split(' ')[0]?.slice(0, 5)}</span>
                    </th>
                  ))}
                  {round?.matchups?.map((m, i) => (
                    <th key={`fs-${i}`} className="text-center py-3 px-2 font-semibold text-[#1B4D3E] bg-[#1B4D3E]/5 min-w-[70px]">
                      FS {i + 1}
                    </th>
                  ))}
                  {round?.matchups?.some(m => m.grandslam?.active) && (
                    <th className="text-center py-3 px-2 font-semibold text-yellow-700 bg-yellow-50 min-w-[50px]">GS</th>
                  )}
                  {hasUA && (
                    <th className="text-center py-3 px-2 font-semibold text-amber-700 bg-amber-50 min-w-[50px]" data-testid="uv-column-header">UV</th>
                  )}
                  <th className="text-center py-3 px-2 font-bold text-[#0F172A] bg-gray-100 min-w-[70px]">Pts</th>
                  <th className="text-center py-3 px-2 font-bold text-green-600 bg-green-50 min-w-[80px]">$</th>
                </tr>
              </thead>
              <tbody>
                {round?.players?.map((player) => {
                  const individualResults = {};
                  round?.individual_matchups?.forEach(m => {
                    if (m.player1_id === player.player_id) {
                      individualResults[m.player2_id] = m.total_points;
                    } else if (m.player2_id === player.player_id) {
                      individualResults[m.player1_id] = -m.total_points;
                    }
                  });
                  
                  const foursomeResults = round?.matchups?.map(m => {
                    const isBase = m.base_pair?.includes(player.player_id);
                    const isSecondary = m.secondary_pair?.includes(player.player_id);
                    const raw = m.total_points_raw ?? m.total_points;
                    if (isBase) return raw;
                    if (isSecondary) return -raw;
                    return null;
                  }) || [];

                  const uvTotal = (round?.matchups || []).reduce((sum, m) => {
                    const adj = m.units_advantage_adjustment || 0;
                    if (!adj) return sum;
                    const isBase = m.base_pair?.includes(player.player_id);
                    const isSecondary = m.secondary_pair?.includes(player.player_id);
                    if (isBase) return sum + adj;
                    if (isSecondary) return sum - adj;
                    return sum;
                  }, 0);
                  
                  const totalIndividual = Object.values(individualResults).reduce((sum, pts) => sum + pts, 0);
                  const totalFoursome = foursomeResults.reduce((sum, pts) => sum + (pts || 0), 0);
                  
                  const gsTotal = (round?.matchups || []).reduce((sum, m) => {
                    if (!m.grandslam?.active) return sum;
                    const isBase = m.base_pair?.includes(player.player_id);
                    const isSecondary = m.secondary_pair?.includes(player.player_id);
                    if (isBase) return sum + m.grandslam.bonus;
                    if (isSecondary) return sum - m.grandslam.bonus;
                    return sum;
                  }, 0);
                  
                  const totalPoints = totalIndividual + totalFoursome + gsTotal + uvTotal;
                  const totalMoney = totalPoints * pointValue;
                  
                  return (
                    <tr key={player.player_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-2 sticky left-0 bg-white">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full shrink-0 ${player.is_base ? 'bg-[#1B4D3E]' : 'bg-[#D4A373]'}`}></span>
                          <span className="font-semibold text-[#0F172A] truncate max-w-[70px] sm:max-w-[120px]" title={player.name}>{player.name.split(' ')[0]}</span>
                        </div>
                      </td>
                      {round?.players?.map(opponent => {
                        if (opponent.player_id === player.player_id) {
                          return <td key={opponent.player_id} className="text-center py-3 px-2 text-gray-300">-</td>;
                        }
                        const pts = individualResults[opponent.player_id] || 0;
                        return (
                          <td key={opponent.player_id} className={`text-center py-3 px-2 font-semibold ${
                            pts > 0 ? 'text-green-600' : pts < 0 ? 'text-red-600' : 'text-gray-400'
                          }`}>
                            {pts > 0 ? '+' : ''}{pts}
                          </td>
                        );
                      })}
                      {foursomeResults.map((pts, i) => (
                        <td key={`fs-${i}`} className={`text-center py-3 px-2 font-semibold bg-[#1B4D3E]/5 ${
                          pts === null ? 'text-gray-300' :
                          pts > 0 ? 'text-green-600' : pts < 0 ? 'text-red-600' : 'text-gray-400'
                        }`}>
                          {pts === null ? '-' : pts > 0 ? '+' + pts : pts}
                        </td>
                      ))}
                      {round?.matchups?.some(m => m.grandslam?.active) && (
                        <td className={`text-center py-3 px-2 font-bold bg-yellow-50 ${
                          gsTotal > 0 ? 'text-green-600' : gsTotal < 0 ? 'text-red-600' : 'text-gray-400'
                        }`} data-testid={`gs-cell-${player.player_id}`}>
                          {gsTotal !== 0 ? (gsTotal > 0 ? '+' + gsTotal : gsTotal) : '0'}
                        </td>
                      )}
                      {hasUA && (
                        <td className={`text-center py-3 px-2 font-bold bg-amber-50 ${
                          uvTotal > 0 ? 'text-green-600' : uvTotal < 0 ? 'text-red-600' : 'text-gray-400'
                        }`} data-testid={`uv-cell-${player.player_id}`}>
                          {uvTotal !== 0 ? (uvTotal > 0 ? '+' + uvTotal : uvTotal) : '0'}
                        </td>
                      )}
                      <td className={`text-center py-3 px-2 font-bold bg-gray-100 ${
                        totalPoints > 0 ? 'text-green-600' : totalPoints < 0 ? 'text-red-600' : 'text-gray-500'
                      }`}>
                        {totalPoints > 0 ? '+' : ''}{totalPoints}
                      </td>
                      <td className={`text-center py-3 px-2 font-bold bg-green-50 ${
                        totalMoney > 0 ? 'text-green-600' : totalMoney < 0 ? 'text-red-600' : 'text-gray-500'
                      }`}>
                        {totalMoney >= 0 ? '+' : ''}${Math.abs(totalMoney).toFixed(0)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          );
        })() : (
          <div className="text-center py-8">
            <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-[#64748B]">No se configuro valor por punto</p>
            <p className="text-sm text-[#64748B] mt-1">Configura el valor al crear la ronda para ver el balance economico</p>
          </div>
        )}
        
        {pointValue > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-[#64748B]">
              <strong>Valor por punto:</strong> ${pointValue} - 
              <span className="text-[#1B4D3E]"> FS = Foursome (sin ventaja)</span> - 
              <span className="text-yellow-700"> GS = Grand Slam</span> - 
              <span className="text-amber-700"> UV = Unidades de Ventaja (se suman o restan)</span> - 
              Positivo = Gana, Negativo = Pierde
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
